import useStreamChat from '@/hook/useStreamChat';
import chatApi from '@/api/chat';
import businessAdminJobsApi from '@/api/businessAdminJobs';
import { channelsType, messageType } from '@/typings/allType';
import { findWrappedSupportChannel } from '@/lib/streamChannelAdapter';
import { Button, DatePicker, Input, Modal, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { useState, MutableRefObject, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { StreamChat } from 'stream-chat';
import './Chat.scss';

type IssueType = 'GENERAL' | 'MISSING' | 'DAMAGED';
const PAUSE_NOTICE_TEXT =
    'Job has been paused by admin. Admin updated this; you will be notified when admin updates. For now, move to another job.';

const parseChannelData = (channel: channelsType | null): Record<string, unknown> => {
    const raw = (channel as any)?.data;
    if (!raw || typeof raw !== 'string') return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const getIssueType = (channel: channelsType | null): IssueType => {
    const issueType = parseChannelData(channel).issueType;
    if (issueType === 'MISSING' || issueType === 'DAMAGED') return issueType;
    return 'GENERAL';
};

const getBusinessAdminJobId = (channel: channelsType | null): string => {
    const data = parseChannelData(channel);
    const id = data.businessAdminJobId ?? data.jobId;
    if (typeof id === 'string' || typeof id === 'number') {
        return String(id).trim();
    }
    return '';
};

const isSystemAdminNotice = (m: any) => {
    if (!m) return false;
    if (m.customType === 'SYSTEM_WARNING' || m.customType === 'SYSTEM_INFO') return true;
    if (!m.data || typeof m.data !== 'string') return false;
    try {
        const parsed = JSON.parse(m.data) as { severity?: string; event?: string };
        return (
            parsed.severity === 'warning' ||
            parsed.severity === 'info' ||
            parsed.event === 'JOB_PAUSED' ||
            parsed.event === 'JOB_RESCHEDULED' ||
            parsed.event === 'JOB_RESUMED'
        );
    } catch {
        return false;
    }
};

const noticeSeverity = (m: any): 'warning' | 'info' | null => {
    if (!m) return null;
    if (m.customType === 'SYSTEM_WARNING') return 'warning';
    if (m.customType === 'SYSTEM_INFO') return 'info';
    if (!m.data || typeof m.data !== 'string') return null;
    try {
        const parsed = JSON.parse(m.data) as { severity?: string; event?: string };
        if (parsed.severity === 'warning' || parsed.event === 'JOB_PAUSED') return 'warning';
        if (parsed.severity === 'info' || parsed.event === 'JOB_RESCHEDULED' || parsed.event === 'JOB_RESUMED')
            return 'info';
        return null;
    } catch {
        return null;
    }
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error === 'string' && error.trim()) {
        return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
        const messageText = (error as { message?: unknown }).message;
        if (typeof messageText === 'string' && messageText.trim()) {
            return messageText;
        }
    }
    return fallback;
};

const Chat = () => {
    const link = useNavigate();
    const { initStreamChat, createChannel, refreshUnreadCount } = useStreamChat();
    const { adminId, streamApiKey, chatAccessToken } = useSelector((state: any) => state.state.userInfo);
    const streamAdminId = String(adminId || '');
    const [channels, setChannels] = useState<channelsType[]>([]);
    const [selectedChannelUrl, setSelectedChannelUrl] = useState('');
    const [msgData, setMsgData] = useState<messageType[]>([]);
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const getTopDate = (time: number, index: number) => {
        const previousDate = index ? dayjs(msgData[index - 1].createdAt).format('YYYY-MM-DD') : null;
        const currentDate = dayjs(time).format('YYYY-MM-DD');
        const today = dayjs().format('YYYY-MM-DD');
        const yesterday = dayjs().add(-1, 'day').format('YYYY-MM-DD');
        if (previousDate !== currentDate || !index) {
            if (currentDate !== today) {
                if (currentDate !== yesterday) {
                    return dayjs(time).format('dddd, DD MMM');
                } else {
                    return 'Yesterday';
                }
            } else {
                return 'Today';
            }
        }
    };
    const { chatUserId, chatuserIdentity, chatUserName } = useSelector((state: any) => state.state.chatUser);
    const messageBox: MutableRefObject<any> = useRef(null);
    const chatContainer: MutableRefObject<any> = useRef(null);
    const currentChannel: MutableRefObject<any> = useRef(null);
    const [messageCollection, setMessageCollection] = useState<{
        dispose: any;
    }>();
    const [historyMessageQuery, setHistoryMessageQuery] = useState<{
        load: any;
    }>();
    const messageKey = (m: any) => {
        // Prefer reqId so pending -> succeeded versions of same message collapse into one.
        if (m?.reqId) return `req:${m.reqId}`;
        if (m?.messageId && Number(m.messageId) > 0) return `mid:${m.messageId}`;
        return `tmp:${m?.createdAt || 0}:${m?.message || ''}`;
    };
    const mergeMessages = (prev: any[], incoming: any[]) => {
        const map = new Map<string, any>();
        [...prev, ...incoming].forEach(item => map.set(messageKey(item), item));
        return Array.from(map.values()).sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));
    };
    const appendChatMessages = (incoming: any[]) => {
        if (!incoming.length) {
            return;
        }
        setMsgData(state => mergeMessages(state, incoming));
    };
    const markActiveChannelRead = async () => {
        if (!currentChannel.current?.markAsRead) {
            return;
        }
        try {
            await currentChannel.current.markAsRead();
            setChannels(prev => [...prev]);
            await refreshUnreadCount();
        } catch (error) {
            //
        }
    };
    const loadHistoryMessage = async () => {
        try {
            setHistoryMessageQuery(
                currentChannel.current.createPreviousMessageListQuery({
                    limit: 20,
                    reverse: false,
                })
            );
            await markActiveChannelRead();
        } catch (error) {
            // channelWrong();
        }
    };
    const isLoadHistory = useRef(false);
    useEffect(() => {
        if (historyMessageQuery) {
            historyMessageQuery.load().then((message: any) => {
                setMessagesLoading(false);
                console.log('historical news:', message);
                if (message.length) {
                    setMsgData(state => mergeMessages(state, message));
                }
            });
        }
    }, [historyMessageQuery]);
    const [taskerInfo, setTaskerInfo] = useState({ name: '', avatar: '' });
    const getChannelUrl = (channel: any) => channel?._url || channel?.url || '';
    const toChannelName = (channel: channelsType) => {
        const channelName = (channel as any)?.name?.trim?.();
        if (channelName) {
            return channelName;
        }
        const names = channel.members
            .filter(m => String(m.userId) !== streamAdminId)
            .map(m => m.nickname || m.userId)
            .filter(Boolean);
        return names.length ? names.join(', ') : 'Group chat';
    };
    const selectChannel = (channel: channelsType) => {
        setSelectedChannelUrl(getChannelUrl(channel));
        setMsgData([]);
        currentChannel.current = channel;
        setMessagesLoading(true);
        initMessage(channel);
        showChatInfo(channel);
    };
    const initMessage = channel => {
        if (messageCollection?.dispose) {
            messageCollection.dispose();
        }
        const collection = currentChannel.current.createMessageCollection({
            startingPoint: Date.now(),
            limit: 100,
        });
        collection.setMessageCollectionHandler(messageHandlers);
        setMessageCollection(collection);
        loadHistoryMessage();
    };
    const [updateMessages, setUpdateMessages] = useState([]);
    useEffect(() => {
        if (updateMessages.length) {
            setMsgData(state => mergeMessages(state, updateMessages as any[]));
        }
    }, [updateMessages]);
    const messageHandlers = {
        // Message change trigger
        onMessagesAdded: (context: any, channel: any, messages: any) => {
            // new message
            console.log(context.source, 'new message:', messages);
            if (
                context.source === 'EVENT_MESSAGE_RECEIVED' ||
                context.source === 'EVENT_MESSAGE_SENT' ||
                context.source === 'EVENT_MESSAGE_SENT_PENDING'
            ) {
                isLoadHistory.current = false;
                setMsgData(state => mergeMessages(state, messages));
                void markActiveChannelRead();
            }
        },
        onMessagesUpdated: (context: any, channel: any, messages: any) => {
            console.log(context.source, 'update message:', messages);
            setUpdateMessages(messages);
        },
        // onMessagesDeleted: (context: any, channel: any, messageIds: any) => {},
        onChannelUpdated: (context: any, channel: any) => {
            //
        },
        // onChannelDeleted: (context: any, channelUrl: any) => {},
        // onHugeGapDetected: () => {},
    };
    const [messageValue, setMessageValue] = useState('');
    const sendMessage = async () => {
        const newValue = messageValue.replace(/\s+/g, '');
        if (!newValue || !currentChannel.current) {
            return;
        }
        const userMessageParams = { message: messageValue };
        setMessageValue('');
        // scrollToBottom();
        currentChannel.current
            .sendUserMessage(userMessageParams)
            .onSucceeded((sentMessage: any) => {
                appendChatMessages([sentMessage]);
                setMessageValue('');
            })
            .onFailed((error: any) => {
                //
            });
    };
    //-------------------------------------------------------------------------------
    useEffect(() => {
        if (!streamAdminId || !streamApiKey || !chatAccessToken) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setChannels([]);
        setSelectedChannelUrl('');
        setMsgData([]);
        initStreamChat(list => {
            if (cancelled) {
                return;
            }
            const openChannel = (channel: channelsType) => {
                if (cancelled) {
                    return;
                }
                setChannels(current => {
                    const channelUrl = getChannelUrl(channel);
                    if (!channelUrl) {
                        return current;
                    }
                    if (current.some(item => getChannelUrl(item) === channelUrl)) {
                        // Replace the stale entry with the freshly-queried channel so updated
                        // member names (e.g. after ensureChatUser) show in the list.
                        return current.map(item => (getChannelUrl(item) === channelUrl ? channel : item));
                    }
                    return [channel, ...current];
                });
                selectChannel(channel);
            };
            const openExistingOrCreate = (loaded: channelsType[]) => {
                if (cancelled) {
                    return;
                }
                setChannels(loaded);
                setLoading(false);
                if (!chatUserId) {
                    if (loaded.length) {
                        openChannel(loaded[0]);
                    }
                    return;
                }
                const openTargetChannel = () => {
                    if (cancelled) {
                        return;
                    }
                    // Re-query (createChannel -> findWrappedChannelForUser) so the channel we open
                    // carries the name ensureChatUser just refreshed — for existing channels too.
                    createChannel([chatUserId, streamAdminId], res => {
                        if (cancelled) {
                            return;
                        }
                        const hasChannel = !!(res as any)?.url || !!(res as any)?._url;
                        if (hasChannel) {
                            openChannel(res);
                            return;
                        }
                        const matched = loaded.find(item =>
                            item.members.some(member => String(member.userId) === String(chatUserId))
                        );
                        if (matched) {
                            openChannel(matched);
                        } else if (loaded.length) {
                            openChannel(loaded[0]);
                        }
                    });
                };
                // Always refresh the target user's GetStream identity (name/avatar) first — including
                // when a channel already exists — so their display name is up to date, then open it.
                // (They may also not have logged in since the migration, in which case this provisions them.)
                chatApi
                    .ensureChatUser(chatUserId, chatuserIdentity)
                    .catch(() => {
                        // Best-effort upsert; still try to open/create the channel on failure.
                    })
                    .finally(openTargetChannel);
            };
            openExistingOrCreate(list);
        });
        return () => {
            cancelled = true;
        };
    }, [streamAdminId, streamApiKey, chatAccessToken, chatUserId, chatuserIdentity]);
    useEffect(() => {
        return () => {
            if (messageCollection?.dispose) {
                messageCollection.dispose();
            }
        };
    }, [messageCollection]);
    useEffect(() => {
        if (messageBox.current) {
            scrollToBottom();
        }
    }, [msgData]);
    const scrollToBottom = () => {
        if (!isLoadHistory.current) {
            messageBox.current.scrollIntoView(false);
        }
    };
    let isLoading = false;
    const messageBoxScroll = async (e: any) => {
        if (chatContainer.current.scrollTop < 20) {
            if (isLoading || msgData.length < 20) return;
            isLoading = true;
            historyMessageQuery!.load().then((message: any) => {
                isLoading = false;
                isLoadHistory.current = true;
                console.log('historyMessage', message);
                message.length && setMsgData(state => mergeMessages(state, message));
            });
        }
    };
    const [sendChat, isSendChat] = useState(true);
    const [jobActionLoading, setJobActionLoading] = useState(false);
    const [currentJobStatus, setCurrentJobStatus] = useState('');
    const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
    const [rescheduleAt, setRescheduleAt] = useState<any>(null);
    const [taskDetail, setTaskDetail] = useState({
        taskTitle: '',
        workStartDate: '',
        workStartTime: '',
        workEndDate: '',
        workEndTime: '',
    });
    const showChatInfo = (channel: channelsType) => {
        const members = channel.members || [];
        const channelName = (channel as any)?.name?.trim?.();
        const names = members
            .filter(i => String(i.userId) !== streamAdminId)
            .map(i => i.nickname)
            .filter(Boolean);
        const firstOther = members.find(i => String(i.userId) !== streamAdminId);
        // Fall back to the full name we already know from the user list (redux) when the
        // GetStream member has no name yet (e.g. a user who never logged in).
        const isTargetChannel = members.some(i => String(i.userId) === String(chatUserId));
        setTaskerInfo({
            name: channelName || (names.length ? names.join(', ') : '') || (isTargetChannel ? chatUserName : ''),
            avatar: firstOther?.plainProfileUrl || '',
        });
        // setTaskerInfo({
        //     name: 'alex',
        //     avatar: 'https://faastaa-bucket.s3.ap-southeast-2.amazonaws.com/2024/04/11/Other@2x.png',
        // });
    };
    const senderName = (m: any) => {
        if (String(m.sender?.userId) === streamAdminId) {
            return 'You';
        }
        return m.sender?.nickname || m.sender?.userId || '';
    };
    const imageUrl = (m: any) => {
        const url = m?.url;
        if (!url || typeof url !== 'string') {
            return '';
        }
        if (typeof m?.type === 'string' && m.type.startsWith('image/')) {
            return url;
        }
        if (/\.(jpe?g|png|gif|webp)(\?|$)/i.test(url)) {
            return url;
        }
        return '';
    };
    const valueChange = e => {
        setMessageValue(e.target.value);
    };
    const selectedChannel =
        channels.find(i => getChannelUrl(i) === selectedChannelUrl) || (currentChannel.current as channelsType | null);
    const issueType = getIssueType(selectedChannel || null);
    const canShowJobActions = !!selectedChannelUrl && (issueType === 'MISSING' || issueType === 'DAMAGED');
    useEffect(() => {
        const loadCurrentStatus = async () => {
            const channel = selectedChannel || null;
            const businessAdminJobId = getBusinessAdminJobId(channel);
            if (!businessAdminJobId) {
                setCurrentJobStatus('');
                return;
            }
            try {
                const detailRes = await businessAdminJobsApi.getJobDetail(businessAdminJobId);
                const detail = detailRes?.data?.data || detailRes?.data;
                setCurrentJobStatus((detail?.productStatus || '').toUpperCase());
            } catch {
                // ignore status load failures in chat
            }
        };
        void loadCurrentStatus();
    }, [selectedChannelUrl]);
    const confirmReschedule = async () => {
        if (!rescheduleAt) {
            message.error('Please select schedule date/time.');
            return;
        }
        await updateBusinessAdminJobStatus('INPROGRESS', dayjs(rescheduleAt).startOf('day').toISOString());
        setRescheduleModalOpen(false);
        setRescheduleAt(null);
    };

    const sendJobNotice = async (
        channel: channelsType,
        params: { message: string; customType: string; data: Record<string, unknown> }
    ) => {
        if (!channel || typeof (channel as any).sendUserMessage !== 'function') {
            return;
        }
        await new Promise<void>(resolve => {
            (channel as any)
                .sendUserMessage({
                    message: params.message,
                    customType: params.customType,
                    data: JSON.stringify(params.data),
                })
                .onSucceeded((sentMessage: any) => {
                    appendChatMessages([sentMessage]);
                    resolve();
                })
                .onFailed(() => resolve());
        });
    };

    const resolveJobNoticeChannel = async (businessAdminJobId: string, fallbackChannel: channelsType | null) => {
        if (!streamApiKey) {
            return fallbackChannel;
        }
        try {
            const client = StreamChat.getInstance(streamApiKey);
            const supportChannel = await findWrappedSupportChannel(client, businessAdminJobId);
            return (supportChannel as unknown as channelsType) || fallbackChannel;
        } catch {
            return fallbackChannel;
        }
    };

    const updateBusinessAdminJobStatus = async (
        productStatus: 'PAUSE' | 'RESCHEDULE' | 'INPROGRESS',
        scheduledAt?: string
    ) => {
        const channel = selectedChannel || null;
        const businessAdminJobId = getBusinessAdminJobId(channel);
        if (!businessAdminJobId) {
            message.error('This chat does not include businessAdminJobId in channel data.');
            return;
        }
        setJobActionLoading(true);
        try {
            await businessAdminJobsApi.updateChatStatus(businessAdminJobId, {
                productStatus,
                ...(scheduledAt ? { scheduledAt } : {}),
            });
            setCurrentJobStatus(productStatus);
            const noticeChannel = (await resolveJobNoticeChannel(businessAdminJobId, channel)) || channel;
            if (productStatus === 'PAUSE' && noticeChannel) {
                await sendJobNotice(noticeChannel, {
                    message: PAUSE_NOTICE_TEXT,
                    customType: 'SYSTEM_WARNING',
                    data: {
                        event: 'JOB_PAUSED',
                        severity: 'warning',
                        source: 'admin',
                        jobId: businessAdminJobId,
                    },
                });
            } else if (scheduledAt && noticeChannel) {
                const schedulePart = scheduledAt
                    ? ` New schedule date: ${dayjs(scheduledAt).format('ddd, DD MMM YYYY')}.`
                    : '';
                await sendJobNotice(noticeChannel, {
                    message: `Admin rescheduled your job.${schedulePart}`.trim(),
                    customType: 'SYSTEM_INFO',
                    data: {
                        event: 'JOB_RESCHEDULED',
                        severity: 'info',
                        source: 'admin',
                        jobId: businessAdminJobId,
                        ...(scheduledAt ? { scheduledAt } : {}),
                    },
                });
            } else if (productStatus === 'INPROGRESS' && noticeChannel) {
                await sendJobNotice(noticeChannel, {
                    message: 'Admin resumed this job. You can now start this job, work on it, and complete it.',
                    customType: 'SYSTEM_INFO',
                    data: {
                        event: 'JOB_RESUMED',
                        severity: 'info',
                        source: 'admin',
                        jobId: businessAdminJobId,
                    },
                });
            }
            message.success(
                productStatus === 'PAUSE'
                    ? 'Job paused.'
                    : scheduledAt
                    ? 'Job rescheduled and moved to in progress.'
                    : 'Job resumed.'
            );
        } catch (error: unknown) {
            message.error(getApiErrorMessage(error, 'Failed to update job status.'));
        } finally {
            setJobActionLoading(false);
        }
    };
    return (
        <div className="page-main">
            {loading ? (
                <div className="data_loading flex-center">
                    <Spin />
                </div>
            ) : (
                <>
                    <div className="page-title flex-between">
                        <div>
                            <img
                                className="back-icon"
                                src={require('@/assets/images/back-icon.png')}
                                alt="back-icon"
                                onClick={() => {
                                    link(-1);
                                }}
                            />
                            Group chat with {chatUserName || chatuserIdentity}
                        </div>
                    </div>
                    <div className="chat-wrap">
                        <div className="chat-list">
                            {channels.length ? (
                                channels.map((channel, index) => (
                                    <div
                                        className={
                                            selectedChannelUrl === getChannelUrl(channel)
                                                ? 'chat-list-item active'
                                                : 'chat-list-item'
                                        }
                                        key={getChannelUrl(channel) || `${toChannelName(channel)}-${index}`}
                                        onClick={() => selectChannel(channel)}
                                    >
                                        <div className="chat-list-title-row">
                                            <div className="chat-list-title">{toChannelName(channel)}</div>
                                            {channel.unreadMessageCount > 0 &&
                                            selectedChannelUrl !== getChannelUrl(channel) ? (
                                                <span className="chat-list-badge">{channel.unreadMessageCount}</span>
                                            ) : null}
                                        </div>
                                        <div className="chat-list-time">
                                            {channel.lastMessage?.createdAt
                                                ? dayjs(channel.lastMessage.createdAt).format('MMM D, h:mm A')
                                                : 'No messages'}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="chat-list-empty">No chats yet</div>
                            )}
                        </div>
                        <div className="chat">
                            <div className="send-user">
                                <div className="user-avatar">
                                    <img
                                        src={
                                            taskerInfo.avatar
                                                ? taskerInfo.avatar
                                                : require('@/assets/images/default-avatar.png')
                                        }
                                        alt="user-avatar"
                                    />
                                </div>
                                <div className="user-name">
                                    <span>{taskerInfo.name || 'Select a chat'}</span>
                                </div>
                                {canShowJobActions ? (
                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                        {currentJobStatus === 'PAUSE' ? (
                                            <Button
                                                type="default"
                                                size="small"
                                                loading={jobActionLoading}
                                                onClick={() => updateBusinessAdminJobStatus('INPROGRESS')}
                                            >
                                                Resume Job
                                            </Button>
                                        ) : (
                                            <Button
                                                type="default"
                                                size="small"
                                                loading={jobActionLoading}
                                                onClick={() => updateBusinessAdminJobStatus('PAUSE')}
                                            >
                                                Pause Job
                                            </Button>
                                        )}
                                        <Button
                                            type="default"
                                            size="small"
                                            loading={jobActionLoading}
                                            onClick={() => setRescheduleModalOpen(true)}
                                        >
                                            Reschedule Job
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                            <div className="msg-box" onScroll={messageBoxScroll} ref={chatContainer}>
                                {messagesLoading ? (
                                    <div className="data_loading flex-center">
                                        <Spin />
                                    </div>
                                ) : null}
                                <div ref={messageBox}>
                                    {msgData.map((m, index) => {
                                        return (
                                            <div key={messageKey(m)}>
                                                <div className="top-date">{getTopDate(m.createdAt, index)}</div>
                                                <div
                                                    className={
                                                        String(m.sender?.userId) === streamAdminId
                                                            ? 'sender-msg'
                                                            : 'other-msg'
                                                    }
                                                >
                                                    {m.sendingStatus === 'pending' ? (
                                                        <img
                                                            src={require('@/assets/images/loading-icon3.png')}
                                                            className="loading-icon"
                                                            alt=""
                                                        />
                                                    ) : (
                                                        ''
                                                    )}
                                                    <div className="msg-content">
                                                        {String(m.sender?.userId) !== streamAdminId ? (
                                                            <div className="sender-name">{senderName(m)}</div>
                                                        ) : null}
                                                        {imageUrl(m) ? (
                                                            <img
                                                                src={imageUrl(m)}
                                                                alt="Image"
                                                                className="msg-image"
                                                                loading="lazy"
                                                            />
                                                        ) : null}
                                                        {m.message ? (
                                                            <div
                                                                className="msg-text"
                                                                style={(() => {
                                                                    if (!isSystemAdminNotice(m)) return undefined;
                                                                    const severity = noticeSeverity(m);
                                                                    if (severity === 'info') {
                                                                        return {
                                                                            background: '#e8f1ff',
                                                                            border: '1px solid #88b4ff',
                                                                            color: '#0f3d91',
                                                                            borderRadius: 8,
                                                                            padding: '8px 10px',
                                                                            fontWeight: 600,
                                                                        };
                                                                    }
                                                                    return {
                                                                        background: '#fff7d6',
                                                                        border: '1px solid #f2cf66',
                                                                        color: '#6b4f00',
                                                                        borderRadius: 8,
                                                                        padding: '8px 10px',
                                                                        fontWeight: 600,
                                                                    };
                                                                })()}
                                                            >
                                                                {m.message}
                                                            </div>
                                                        ) : null}
                                                        <div className="send-time">
                                                            {dayjs(m.createdAt).format('h:mm A')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="send-box">
                                <Input
                                    className="send-input"
                                    value={messageValue}
                                    placeholder="Message..."
                                    maxLength={1000}
                                    onPressEnter={sendMessage}
                                    onChange={valueChange}
                                    disabled={!selectedChannelUrl}
                                />
                                <img
                                    className="send-btn"
                                    src={require('@/assets/images/send-msg.png')}
                                    alt="send-msg"
                                    onClick={sendMessage}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
            <Modal
                title="Reschedule Job"
                open={rescheduleModalOpen}
                centered
                maskClosable={false}
                onCancel={() => {
                    setRescheduleModalOpen(false);
                    setRescheduleAt(null);
                }}
                footer={null}
            >
                <DatePicker
                    style={{ width: '100%' }}
                    value={rescheduleAt}
                    onChange={v => setRescheduleAt(v)}
                    placeholder="Pick new schedule date"
                />
                <div className="flex-center" style={{ marginTop: 20, columnGap: 12, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => {
                            setRescheduleModalOpen(false);
                            setRescheduleAt(null);
                        }}
                        disabled={jobActionLoading}
                    >
                        Cancel
                    </Button>
                    <Button type="primary" loading={jobActionLoading} onClick={confirmReschedule}>
                        Confirm Schedule
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
export default Chat;
